import { api } from '../config/api';

export interface BlogPost {
  id: string;
  title: string;
  content: string;
  excerpt: string;
  author: string;
  featured_image?: string;
  status: "draft" | "published";
  tags: string[];
  slug: string;
  views: number;
  user_id: string;
  created_at: string;
  updated_at: string;
}

export interface CreateBlogPostData {
  title: string;
  content: string;
  excerpt?: string;
  author: string;
  featured_image?: string;
  status: "draft" | "published";
  tags?: string[];
}

export interface UpdateBlogPostData extends Partial<CreateBlogPostData> {}

export interface BlogPostListResponse {
  data: BlogPost[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number;
  to: number;
}

class BlogPostService {
  private baseUrl = '/blog-posts';

  // Get all blog posts with pagination and filters
  async getPosts(params?: {
    per_page?: number;
    page?: number;
    status?: 'draft' | 'published';
    search?: string;
    tag?: string;
  }): Promise<BlogPostListResponse> {
    const response = await api.get<BlogPostListResponse>(this.baseUrl, { params });
    return response.data;
  }

  // Get a single blog post by ID
  async getPost(id: string): Promise<BlogPost> {
    const response = await api.get<BlogPost>(`${this.baseUrl}/${id}`);
    return response.data;
  }

  // Get a single blog post by slug
  async getPostBySlug(slug: string): Promise<BlogPost> {
    const response = await api.get<BlogPost>(`/blog/slug/${slug}`);
    return response.data;
  }

  // Create a new blog post
  async createPost(data: CreateBlogPostData): Promise<BlogPost> {
    const response = await api.post<BlogPost>(this.baseUrl, data);
    return response.data;
  }

  // Update an existing blog post
  async updatePost(id: string, data: UpdateBlogPostData): Promise<{ message: string }> {
    const response = await api.put<{ message: string }>(`${this.baseUrl}/${id}`, data);
    return response.data;
  }

  // Delete a blog post
  async deletePost(id: string): Promise<{ message: string }> {
    const response = await api.delete<{ message: string }>(`${this.baseUrl}/${id}`);
    return response.data;
  }

  // Publish a blog post
  async publishPost(id: string): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>(`${this.baseUrl}/${id}/publish`);
    return response.data;
  }

  // Unpublish a blog post
  async unpublishPost(id: string): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>(`${this.baseUrl}/${id}/unpublish`);
    return response.data;
  }

  // Get posts by user
  async getUserPosts(userId: string, params?: {
    per_page?: number;
    page?: number;
  }): Promise<BlogPostListResponse> {
    const response = await api.get<BlogPostListResponse>(`${this.baseUrl}/user/${userId}`, { params });
    return response.data;
  }

  // Search published posts (public)
  async getPublishedPosts(params?: {
    per_page?: number;
    page?: number;
    search?: string;
    tag?: string;
  }): Promise<BlogPostListResponse> {
    const response = await api.get<BlogPostListResponse>('/blog', { params: { ...params, status: 'published' } });
    return response.data;
  }
}

export const blogPostService = new BlogPostService();
